/**
 * 入力データまわりの処理
 * - 占い師IDや応募ジャンル(category)などから必要な入力データの判定
 * - アカウント情報とPOSTデータの結合、v-modelへの反映
 * - 不要な入力情報を削除
 */

import { mapState, mapGetters } from 'vuex';
import { FORM_LABELS, FORM_DATA, BIRTHTIME_UNKNOWN_VALUE } from '@/CONSTS';
import * as type from '@/utvStore/store/mutation-types';
import fetchAccountData from '@/utils/fetchAccountData';
import GuestTemporaryInput from '@/utils/GuestTemporaryInput';
import categoryConfig from '../data/categoryConfig';

const entryClone = Object.assign({}, FORM_DATA.entry);

export default {
  data() {
    return {
      userProfile: {},
      registedColumn: {},
      FORM_LABELS,
      // 入力項目データ
      self: FORM_DATA.self,
      target: FORM_DATA.target,
      palm: FORM_DATA.palm,
      entry: FORM_DATA.entry,
      auth: FORM_DATA.auth
    };
  },

  props: {
    postParams: {
      type: [Object, Array],
      default: () => ({})
    },
    // formdata関連
    action: String,
    method: {
      type: String,
      default: 'post'
    },
    itemsId: [Number, String],
    itemScheduleId: [Number, String],
    inputParam: Object,
    categoryId: [Number, String],
    guestId: String,

    // ログインフォームに渡す値
    reissueKey: String,
    returnPath: {
      type: String,
      default: window.location.href
    },
    entryDetailRequired: Boolean,
    isRandSelect: Boolean,
    orderGenre: String,
    expText: String,
    returnParams: {
      type: [Object, Array],
      default: () => ({})
    },
    inputEntryType: [Number, String],
    movieCustomPlanningId: [Number, String]
  },

  computed: {
    ...mapState({
      accessToken: state => state.auth.accessToken
    }),
    isLoggedIn() {
      return Boolean(this.accessToken);
    }
  },

  watch: {
    userProfile: {
      // ユーザー情報取得のタイミングでv-modelの更新と登録済みプロフィールの表示パラメータへの代入を行う
      handler() {
        this.setValuesToModels();
        this.setRegistedColumn();
      },
      deep: true
    }
  },

  methods: {
    /**
     * v-model: inputの初期化処理
     * 1 アカウントAPIから最新のプロフィール取得
     * 2 各inputのv-modelへ値を代入
     * 3 登録済みプロフィール表示用のデータ(this.registedColumn)を作成
     **/
    initializeInputData() {
      this.getUserProfile().then(this.setValuesToModels());
      this.setRegistedColumn();
      // 動画の場合はニックネーム入力欄に補足説明を挿入
      const movieActions = ['/movie/lottery/check', '/movie/firstcome/check', '/lp/movie_yoshimoto/check'];
      if (movieActions.indexOf(this.action) !== -1) {
        this.self.nickname.aside = '※ビデオレターの中でお呼びするお名前です';
      }
      // ゲストと登録済みユーザーでバリデーション条件を変化させる項目
      if (this.isLoggedIn) {
        this.auth.email.rules.duplicateEmail = false;
        Object.assign(this.auth.password.rules, {
          required: false,
          max: 100,
          min: 0,
          passCheck2: false,
          passCheck1: false
        });
      } else {
        Object.assign(this.auth.password.rules, {
          required: true,
          max: 30,
          min: 8,
          passCheck2: true,
          passCheck1: true
        });
      }
    },
    getUserProfile() {
      return new Promise((resolve, reject) => {
        if (this.accessToken) {
          return fetchAccountData.getProfile(this.accessToken, this.$store).then(res => {
            if (res.response === 'OK') {
              for (let key in res) {
                // 姓名判断用の処理を追加
                if (key === 'first_name') {
                  this.$set(this.userProfile, 'first_name_seimei', res[key]);
                }
                if (key === 'last_name') {
                  this.$set(this.userProfile, 'last_name_seimei', res[key]);
                }

                this.$set(this.userProfile, key, res[key]);
              }
              return resolve();
            }
          });
        } else {
          // 非ログインの場合何もしない
          return resolve();
        }
      });
    },
    /**
     * 各入力欄のv-modelに値を代入
     * (代入の優先順位)
     * 1. POST dataに入っている値
     * 2. /api/user/info/から取得した値
     * 3. sessionStorageの値
     */
    setValuesToModels() {
      let inputs = [this.self, this.target, this.palm, this.entry, this.auth];
      const tmpData = GuestTemporaryInput.data;
      inputs.forEach(input => {
        for (let key in input) {
          // @CONSTS/FORM_DATA.jsの形式に合わせる必要があるデータの整理
          if (key.includes('birthtime')) {
            let unknownColumnName = input[key].unknownName;
            let value = {
              value: BIRTHTIME_UNKNOWN_VALUE,
              unknown: 1
            };
            if (tmpData[key]) {
              Object.assign(value, tmpData[key]);
            }
            if (this.userProfile[key]) {
              Object.assign(value, {
                value: this.userProfile[key],
                unknown: Number(this.userProfile[unknownColumnName] || 1)
              });
            }
            if (this.postParams[key]) {
              Object.assign(value, {
                value: this.postParams[key],
                unknown: Number(this.postParams[unknownColumnName] || 1)
              });
            }
            Object.assign(input[key], { value });
          } else if (key.includes('entry_genre')) {
            // なんでもお悩み相談の$postParam対応
            if (this.postParams[key]) {
              input[key].value = this.postParams[key];
              this.handleChangeEntryInput(key, input[key]);
            }
          } else if (key.includes('palm')) {
            // 手相
            Object.assign(input[key], {
              storagePath: this.postParams.storage_path,
              values: {
                image_url_palm_left: this.postParams.image_url_palm_left,
                image_url_palm_left_overall: this.postParams.image_url_palm_left_overall,
                image_url_palm_right: this.postParams.image_url_palm_right,
                image_url_palm_right_overall: this.postParams.image_url_palm_right_overall
              }
            });
          } else {
            input[key].value = this.postParams[key] || this.userProfile[key] || tmpData[key];
          }
        }
      });
      // 相談内容など $postParams に入っているデータを代入する
      for (let key in this.entry) {
        this.entry[key].value = this.postParams[key];
      }
    },
    /**
     * $inputParamを参照し、不要なカラムを表示しないようにする
     **/
    deleteUnuseColumn() {
      // 占うのに必要な占術パラメータ以外のキー削除する
      let inputs = [this.self, this.target];
      inputs.forEach(input => {
        for (let key in input) {
          if (!this.inputParam[key]) {
            this.$delete(input, key);
            this.$delete(this.registedColumn, key);
          }
        }
      });
    },
    /**
     * category_idをもとに設問内容を作成
     */
    setEntryQuestions(category_id, input_entry_type = 1, is_uragei = 0) {
      let $input_entry_type = input_entry_type;

      if (
        category_id === 6 &&
        !($input_entry_type === 3 || $input_entry_type === 4 || $input_entry_type === 5 || $input_entry_type === 6 || $input_entry_type === 7)
      ) {
        $input_entry_type = 0;
      }

      // category_idが引けない場合entryを表示しない
      if (!category_id) {
        this.entry = {};
        return Promise.resolve();
      }
      const categoryData = categoryConfig.questions.find(
        question => question.category_id === category_id && question.input_entry_type === $input_entry_type
      );
      Object.keys(this.entry).forEach(key => {
        if (categoryData.questions.indexOf(key) === -1) {
          this.$delete(this.entry, key);
        }
      });

      // categoryがない場合entry_detail
      if (this.categoryId == 5 && this.entry.entry_detail) {
        this.entry.entry_detail.placeholder = categoryConfig.mapPlaceholder(category_id);
        this.entry.entry_detail.aside = `
          ご入力いただく内容は「相談したいテーマ」に沿った内容にしてください。
          <br>「相談したいテーマ」とご質問内容が一致しない場合や、複数の質問を入力いただいた場合、占い師がすべての質問に答えられない可能性があります。
          <br><br>※特定のお相手（恋愛相手/職場の上司など）を占いたい場合、生年月日が分かればご入力いただくことをオススメいたします。
          <br>※その他、お申し込み内容にあわせて、交際期間、これまでに付き合った人数、恋人がいない期間、転職回数などの情報もわかれば、ぜひご入力ください
          <br><br><b>（入力サンプル）</b><br>3年前から付き合っている彼がいます。彼の生年月日は1984年6月3日です。年齢的にそろそろ結婚したいと思うのですが、彼があまり乗り気ではないように見えます。私は彼と結婚できるのでしょうか？また結婚できるとしたら時期についても教えてもらいたいです。
        `;
      }
      if (
        category_id === 6 &&
        !($input_entry_type === 3 || $input_entry_type === 4 || $input_entry_type === 5 || $input_entry_type === 6 || $input_entry_type === 7)
      ) {
        this.entry.praise_detail.placeholder = categoryConfig.mapPlaceholder(category_id);
        this.entry.praise_detail.aside = `ご入力いただいた情報を元に占わせていただきます。`;
        this.entry.praise_detail.rules.required = is_uragei ? true : false;
      }
    },
    /**
     * 登録済みプロフィール表示用データの作成
     */
    setRegistedColumn() {
      // ログイン済みの場合は入力済みパラメータを定義
      if (this.isLoggedIn) {
        let columns = Object.assign({}, this.self, this.target);
        Object.keys(columns).forEach(key => {
          // キーが存在しない場合は処理しない
          if (!this.userProfile.hasOwnProperty(key)) {
            return;
          }

          let value = this.userProfile[key];
          // 出生時間は不明フラグつきのオブジェクトとして定義する
          if (key.includes('birthtime')) {
            if (key === 'birthtime') {
              value = {
                value: this.userProfile.birthtime,
                unknown: Number(this.userProfile.birthtime_unknown)
              };
            }
            if (key === 'target_birthtime') {
              value = {
                value: this.userProfile.target_birthtime,
                unknown: Number(this.userProfile.target_birthtime_unknown)
              };
            }
            // どちらも一度も入力したことがなければnullとして扱う
            if (value.value === null && value.unknown === null) {
              value = null;
            }
          }

          // 空のデータも格納する様に変更
          // if (String(value) && value !== null) {
          this.registedColumn[key] = value;
          // }
        });
      }
    },
    /**
     * ジャンルに応じたヒアリング項目の表示制御
     */
    handleChangeEntryInput(name, field) {
      let questions = [];

      if (name === 'love_status') {
        if (field.value !== '独身' || this.entry['love_status'].value !== '独身') {
          // 「恋人がいない期間」の入力エリアを消す
          this.setEntryFormLoveStatusIsNotSingle();
        } else {
          // entry_genre以外の入力項目を一旦リセット
          this.resetEntryFormOtherEntryGenre();

          // 選択されたカテゴリから質問内容を出し直す
          this.setOneCategoryQuestionForm('結婚について');
        }
      }

      if (name === 'entry_genre') {
        // entry_genre以外の入力項目を一旦リセット
        this.resetEntryFormOtherEntryGenre();

        // 選択されたカテゴリから質問内容を出し直す
        this.setOneCategoryQuestionForm(field.value);

        if (this.entry['love_status']) {
          if (!(this.entry['love_status'].value == undefined || this.entry['love_status'].value == '独身')) {
            // 「恋人がいない期間」の入力エリアを消す
            this.setEntryFormLoveStatusIsNotSingle();
          }
        }
      }
    },
    setCaretPosition(ctrl, pos) {
      ctrl.focus();
      ctrl.setSelectionRange(pos, pos);
    },
    resetEntryFormOtherEntryGenre() {
      Object.keys(this.entry).forEach(key => {
        if (key !== 'entry_genre') {
          this.$delete(this.entry, key);
        }
      });
    },
    setOneCategoryQuestionForm(category_str) {
      let questions = [];
      let categoryObj = categoryConfig.questions.find(q => q.category_name === category_str);
      if (categoryObj) {
        questions = categoryObj.questions;
        questions.forEach(q => {
          this.$set(this.entry, q, entryClone[q]);
        });
        if (this.entry.entry_detail) {
          this.entry.entry_detail.placeholder = categoryConfig.mapPlaceholder(categoryObj.category_id);
        }
      } else {
        this.$set(this.entry, 'entry_detail', entryClone['entry_detail']);
      }
    },
    setEntryFormLoveStatusIsNotSingle() {
      Object.keys(this.entry).forEach(key => {
        if (key === 'lover_search_period') {
          this.$delete(this.entry, key);
        }
      });
    }
  }
};



// WEBPACK FOOTER //
// ./resources/assets/js/utvStore/components/EntryForm/mixins/dataControl.js